import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useContentControls, useContentControlsLoaded, isPageEnabled, getPageRedirect } from "./hooks/useContentControls";
import useSocialFlags, { isSocialVisibleForUser, canViewLanding } from "./hooks/useSocialFlags";
import useEditorialStudioFlags, { isStudioVisibleForUser } from "./hooks/useEditorialStudioFlags";
import { useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import ErrorBoundary from "./components/ErrorBoundary";
import EditorErrorBoundary from "./components/EditorErrorBoundary";
// AIEditorPanel moved to DashboardPage (admin only)
import Layout from "./components/Layout";
import ScrollToTop from "./components/ScrollToTop";
import HomePage from "./pages/HomePage";
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const RubriquePage = lazy(() => import("./pages/RubriquePage"));
const ArtisteMagazinePage = lazy(() => import("./pages/ArtisteMagazinePage"));
const EventsDiscoveryPage = lazy(() => import("./pages/EventsDiscoveryPage"));
const ArtisteProfilPage = lazy(() => import("./pages/ArtisteProfilPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const MyArticlesPage = lazy(() => import("./pages/MyArticlesPage"));
const PodcastsPage = lazy(() => import("./pages/PodcastsPage"));
import GoogleCallbackPage from "./pages/GoogleCallbackPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
const LivePage = lazy(() => import("./pages/LivePage"));
import NotFoundPage from "./pages/NotFoundPage";
const LegalPage = lazy(() => import("./pages/LegalPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const SubscriptionSuccessPage = lazy(() => import("./pages/SubscriptionSuccessPage"));
import "./App.css";

// Lazy load admin/editor pages — chargées uniquement quand nécessaire
const EditorPage = lazy(() => import("./pages/EditorPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const DraftsAdminPage = lazy(() => import("./pages/DraftsAdminPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const SourcesPage = lazy(() => import("./pages/SourcesPage"));
const SocialPage = lazy(() => import("./pages/SocialPage"));
const AdsPage = lazy(() => import("./pages/AdsPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const EmailSettingsPage = lazy(() => import("./pages/EmailSettingsPage"));
const AIProvidersPage = lazy(() => import("./pages/AIProvidersPage"));
const PenseeDuJourPage = lazy(() => import("./pages/PenseeDuJourPage"));
const StudioImportPage = lazy(() => import("./pages/StudioImportPage"));
const AgentDashboardPage = lazy(() => import("./pages/AgentDashboardPage"));
const ContactsPage = lazy(() => import("./pages/ContactsPage"));
const BlogDashboardPage = lazy(() => import("./pages/BlogDashboardPage"));
const NewsletterPage = lazy(() => import("./pages/NewsletterPage"));
const AgentSkillsPage = lazy(() => import("./pages/AgentSkillsPage"));
const AgentsControlCenterPage = lazy(() => import("./pages/AgentsControlCenterPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogArticlePage = lazy(() => import("./pages/BlogArticlePage"));
const PuckPageEditor = lazy(() => import("./components/PuckPageEditor"));
const BuzzPage = lazy(() => import("./pages/BuzzPage"));
const VideoStudioPage = lazy(() => import("./pages/VideoStudioPage"));
const MonteurPage = lazy(() => import("./pages/MonteurPage"));
const StudioPage = lazy(() => import("./pages/StudioPage"));
const ReporterStudioPage = lazy(() => import("./pages/ReporterStudioPage"));
const PlatformAuditPage = lazy(() => import("./pages/PlatformAuditPage"));
const InternalDocsPage = lazy(() => import("./pages/InternalDocsPage"));
const ExtractorPage = lazy(() => import("./pages/ExtractorPage"));
const MarketsAdminPage = lazy(() => import("./pages/MarketsAdminPage"));
const TicketsAdminPage = lazy(() => import("./pages/TicketsAdminPage"));
const CreativeStudioAdminPage = lazy(() => import("./pages/CreativeStudioAdminPage"));
const TicketsCheckoutResultPage = lazy(() => import("./pages/TicketsCheckoutResultPage"));
const ReporterAllowlistPage = lazy(() => import("./pages/ReporterAllowlistPage"));
const ZapChapeauPublicPage = lazy(() => import("./pages/ZapChapeauPublicPage"));
const EmbedZapChapeauPage = lazy(() => import("./pages/EmbedZapChapeauPage"));
const SynthesePage = lazy(() => import("./pages/SynthesePage"));
const MarketsPage = lazy(() => import("./pages/MarketsPage"));
const MarketLearnPage = lazy(() => import("./pages/MarketLearnPage"));
const PublicProfilePage = lazy(() => import("./pages/PublicProfilePage"));
// Ancienne page feed communautaire conservée sur disque (SocialLandingPage.jsx),
// non routée depuis que /social rend la landing premium. À re-router plus tard
// derrière features.social.enabled + social_feed/social_posts.
const SocialLandingPremiumPage = lazy(() => import("./pages/SocialLandingPremiumPage"));
const BilletterieLandingPage = lazy(() => import("./pages/BilletterieLandingPage"));
const EventPublicPage = lazy(() => import("./pages/EventPublicPage"));
const CreateEventPage = lazy(() => import("./pages/CreateEventPage"));
const OrganizerDashboardPage = lazy(() => import("./pages/OrganizerDashboardPage"));
const BilletterieSettingsPage = lazy(() => import("./pages/BilletterieSettingsPage"));

// Spinner partagé des gardes de route — affiché tant que les flags runtime
// (content_controls) ne sont pas chargés, pour ne JAMAIS rediriger sur l'état
// initial FALLBACK (anti-race, cf. useContentControlsLoaded).
const RouteLoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
  </div>
);

// Garde route /dashboard/social — admin uniquement, et accepte aussi
// l'admin en mode preview (features.social.admin_preview_enabled=true).
// Attend le chargement des content-controls avant de décider (anti-race) :
// useSocialFlags dérive de useContentControls → au 1er rendu tous les flags
// sont false (FALLBACK) et déclencheraient un <Navigate to="/"> prématuré.
function SocialAdminRouteGuard() {
  const { isAdmin } = useAuth();
  const flags = useSocialFlags();
  const ccLoaded = useContentControlsLoaded();
  if (!ccLoaded) return <RouteLoadingSpinner />;
  return isSocialVisibleForUser(flags, isAdmin)
    ? <SocialPage />
    : <Navigate to="/" replace />;
}

// Garde route /social — landing premium publique. La landing est visible si la
// VITRINE publique est ouverte (features.social.public_landing), OU si le module
// est réellement ouvert (features.social.enabled), OU si un admin est en mode
// preview (features.social.admin_preview_enabled) — cf. canViewLanding.
// Sinon redirection vers / pour tous. Le feed/composer/upload/IA restent gatés
// section par section À L'INTÉRIEUR de la page (helpers canViewFeed/canPost/…) :
// la landing peut s'afficher en vitrine sans qu'aucune fonctionnalité réelle ne
// soit activée. ATTEND le chargement réel des content-controls avant de décider
// (anti-race) : useSocialFlags dérive de useContentControls → au 1er rendu tous
// les flags sont false (FALLBACK) et déclencheraient un <Navigate> prématuré.
function SocialLandingGuard() {
  const { isAdmin } = useAuth();
  const flags = useSocialFlags();
  const ccLoaded = useContentControlsLoaded();
  if (!ccLoaded) return <RouteLoadingSpinner />;
  return canViewLanding(flags, isAdmin)
    ? <SocialLandingPremiumPage />
    : <Navigate to="/" replace />;
}

// Garde route /dashboard/studio — Editorial Studio (ES1.e). Visible si le module
// est ouvert (features.editorial_studio.enabled) OU si un admin est en preview
// (admin_preview_enabled). ATTEND le chargement réel des content-controls avant
// de décider (anti-race, règle CLAUDE.md PR #159) — le rôle editor est ensuite
// exigé par le backend (403), la garde ne juge que la VISIBILITÉ.
function StudioRouteGuard() {
  const { isAdmin } = useAuth();
  const flags = useEditorialStudioFlags();
  const ccLoaded = useContentControlsLoaded();
  if (!ccLoaded) return <RouteLoadingSpinner />;
  return isStudioVisibleForUser(flags, isAdmin)
    ? <StudioPage />
    : <Navigate to="/" replace />;
}

// Garde routes /marches et /marches/apprendre/:slug. ATTEND le chargement réel
// des content-controls avant de décider : sinon, au chargement dur (refresh,
// URL directe, lien externe), le 1er rendu voit cc=FALLBACK (markets.visible=false)
// et déclenche un <Navigate to=redirect_to> AVANT l'arrivée des vrais flags —
// renvoyant vers la home alors que la page Marchés est en réalité activée.
function MarketsRouteGuard({ children }) {
  const cc = useContentControls();
  const ccLoaded = useContentControlsLoaded();
  if (!ccLoaded) return <RouteLoadingSpinner />;
  return isPageEnabled(cc, 'markets')
    ? children
    : <Navigate to={getPageRedirect(cc, 'markets')} replace />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <ErrorBoundary>
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" /></div>}>
              <Routes>
                {/* Pages plein écran, hors du Layout */}
                <Route path="/admin/page-editor" element={<PuckPageEditor />} />
                <Route path="/live" element={<LivePage />} />
                <Route path="/live/:id" element={<LivePage />} />
                <Route path="/zap" element={<ZapChapeauPublicPage />} />
                <Route path="/zap-chapeau" element={<ZapChapeauPublicPage />} />
                <Route path="/embed/zap-chapeau" element={<EmbedZapChapeauPage />} />
                {/* Retour Stripe Checkout — pages informatives (aucun billet affiché) */}
                <Route path="/tickets/checkout/success" element={<TicketsCheckoutResultPage variant="success" />} />
                <Route path="/tickets/checkout/cancel" element={<TicketsCheckoutResultPage variant="cancel" />} />
                <Route path="/" element={<Layout />}>
                  <Route index element={<HomePage />} />
                  <Route path="article/:id" element={<ArticlePage />} />
                  <Route path="category/:slug" element={<CategoryPage />} />
                  <Route path="billetterie" element={<BilletterieLandingPage />} />
                  <Route path="billetterie/creer" element={<CreateEventPage />} />
                  <Route path="billetterie/dashboard" element={<OrganizerDashboardPage />} />
                  <Route path="event/:id" element={<EventPublicPage />} />
                  <Route path="events" element={<EventsDiscoveryPage />} />
                  <Route path="artiste" element={<ArtisteMagazinePage />} />
                  <Route path="artiste/p/:slug" element={<ArtisteProfilPage />} />
                  <Route path="diaspora" element={<RubriquePage slug="diaspora" />} />
                  <Route path="search" element={<SearchPage />} />
                  <Route path="login" element={<LoginPage />} />
                  <Route path="register" element={<RegisterPage />} />
                  <Route path="auth/google/callback" element={<GoogleCallbackPage />} />
                  <Route path="forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="reset-password" element={<ResetPasswordPage />} />
                  <Route path="verify-email" element={<VerifyEmailPage />} />
                  <Route path="editor" element={<EditorErrorBoundary><EditorPage /></EditorErrorBoundary>} />
                  <Route path="editor/:id" element={<EditorErrorBoundary><EditorPage /></EditorErrorBoundary>} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="dashboard/drafts" element={<DraftsAdminPage />} />
                  <Route path="dashboard/settings" element={<SettingsPage />} />
                  <Route path="dashboard/sources" element={<SourcesPage />} />
                  <Route path="dashboard/social" element={<SocialAdminRouteGuard />} />
                  <Route path="dashboard/ads" element={<AdsPage />} />
                  <Route path="dashboard/email-settings" element={<EmailSettingsPage />} />
                  <Route path="dashboard/ai-providers" element={<AIProvidersPage />} />
                  <Route path="dashboard/import" element={<StudioImportPage />} />
                  <Route path="pensee-du-jour" element={<PenseeDuJourPage />} />
                  <Route path="pensee/:slug" element={<PenseeDuJourPage />} />
                  <Route path="dashboard/analytics" element={<AnalyticsPage />} />
                  <Route path="dashboard/agent" element={<AgentDashboardPage />} />
                  <Route path="dashboard/agents-control" element={<AgentsControlCenterPage />} />
                  <Route path="dashboard/contacts" element={<ContactsPage />} />
                  <Route path="dashboard/blog" element={<BlogDashboardPage />} />
                  <Route path="dashboard/newsletter" element={<NewsletterPage />} />
                  <Route path="dashboard/agent-skills" element={<AgentSkillsPage />} />
                  <Route path="dashboard/video-studio" element={<VideoStudioPage />} />
                  <Route path="dashboard/video-editor" element={<MonteurPage />} />
                  <Route path="dashboard/studio" element={<StudioRouteGuard />} />
                  <Route path="dashboard/reporter-studio" element={<ReporterStudioPage />} />
                  <Route path="dashboard/platform-audit" element={<PlatformAuditPage />} />
                  <Route path="dashboard/docs" element={<InternalDocsPage />} />
                  <Route path="dashboard/extractor" element={<ExtractorPage />} />
                  <Route path="dashboard/markets" element={<MarketsAdminPage />} />
                  <Route path="dashboard/tickets" element={<TicketsAdminPage />} />
                  <Route path="dashboard/billetterie-settings" element={<BilletterieSettingsPage />} />
                  <Route path="dashboard/creative" element={<CreativeStudioAdminPage />} />
                  <Route path="dashboard/reporter-allowlist" element={<ReporterAllowlistPage />} />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route path="u/:handle" element={<PublicProfilePage />} />
                  <Route path="social" element={<SocialLandingGuard />} />
                  <Route path="my-articles" element={<MyArticlesPage />} />
                  <Route path="podcasts" element={<PodcastsPage />} />
                  <Route path="mentions-legales" element={<LegalPage type="mentions-legales" />} />
                  <Route path="cgu" element={<LegalPage type="cgu" />} />
                  <Route path="confidentialite" element={<LegalPage type="confidentialite" />} />
                  <Route path="cookies" element={<LegalPage type="cookies" />} />
                  <Route path="charte-ia" element={<LegalPage type="charte-ia" />} />
                  <Route path="abonnement" element={<PricingPage />} />
                  <Route path="abonnement/success" element={<SubscriptionSuccessPage />} />
                  <Route path="blog" element={<BlogPage />} />
                  <Route path="blog/article/:id" element={<BlogArticlePage />} />
                  <Route path="blog/:category" element={<BlogPage />} />
                  <Route path="synthese" element={<SynthesePage />} />
                  <Route
                    path="marches"
                    element={<MarketsRouteGuard><MarketsPage /></MarketsRouteGuard>}
                  />
                  <Route
                    path="marches/apprendre/:slug"
                    element={<MarketsRouteGuard><MarketLearnPage /></MarketsRouteGuard>}
                  />
                  <Route path="buzz" element={<BuzzPage />} />
                  <Route path="a-propos" element={<AboutPage />} />
                  <Route path="preview" element={<HomePage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Routes>
            </Suspense>
          </ErrorBoundary>
          <Toaster position="bottom-right" />
          {/* AIEditorPanel is now inside DashboardPage */}
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
